import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";

import { Cart } from "@/components/icons";
import { ITableRowExtended } from "@/services/interfaces";
import Checkbox from "@/components/inputs/Checkbox.vue";

import {
  CELL_ACTION,
  CELL_ACTIVE,
  CELL_DEFAULT,
  CELL_DIMMED,
  CELL_NAME,
  CELL_SKU,
  CELL_MANUFACTURER,
  CELL_PRICE,
  CELL_STOCK,
  CELL_TOTAL,
  CELL_SELECT,
} from "./const";

import {
  Cells,
  Buttons,
  TableRowList,
  UpperCategory,
  TableRowFile,
} from "./blocks";
import { Column, IQuantities } from "./interfaces";
import { View } from "../../enums";

import "./RowPicker.scss";

import {
  cartModule,
  personalModule,
  pricesModule,
} from "@/plugins/store-accessors";
import { calculateTotal, isDiscounted } from "@/store/cart/utils";
import { getTooltips } from "./utils";

@Component({
  name: "UpperCategory",
  components: {
    cart: Cart,
    "buttons-cart": Buttons.CartTable,
    "buttons-catalog": Buttons.CatalogTable,
    "cell-name": Cells.Name,
    "cell-action-quantity": Cells.ActionQuantity,
    "cell-action-guest": Cells.ActionGuest,
    "cell-highlighted": Cells.HighLighted,
    "table-list": TableRowList,
    "upper-cat": UpperCategory,
    "table-file": TableRowFile,
    Checkbox,
  },
})
class RowPicker extends Vue {
  @Prop({ type: Object, default: () => ({}) })
  readonly row!: ITableRowExtended;

  @Prop({ type: Array, default: () => [] })
  readonly columns!: Column[];

  @Prop({ type: Object, default: () => ({}) })
  readonly productQuantities!: IQuantities;

  @Prop({ type: Function, default: () => null })
  readonly setQuantity!: (payload: {
    id: number;
    val: number;
    priceType?: number;
  }) => any;

  @Prop({ type: Function, default: () => null })
  readonly addToCart!: (payload: {
    product_id: number;
    quantity: number;
    price_type?: number;
  }) => any;

  @Prop({ type: Function, default: () => null })
  readonly removeFromCart!: (id: number) => any;

  @Prop({ type: String, default: "" })
  readonly highlightText!: string;

  @Prop({ type: String, default: "" })
  readonly highlightColumn!: string | null;

  @Prop({ type: String, default: "table" })
  readonly viewType!: View;

  @Prop({ type: String, default: "catalog" })
  readonly context!: string;

  @Prop({ type: Array, default: () => [] })
  readonly excludedIds!: number[];

  get getProductIds() {
    return cartModule.getProductIds;
  }

  get isAuthorized() {
    return personalModule.isAuthorized;
  }

  get quantity() {
    return this.productQuantities ? this.productQuantities[this.row.id] : 1;
  }

  get isInCart() {
    return this.getProductIds.includes(this.row.id);
  }

  get isCart() {
    return this.$route.name?.includes("cart");
  }

  get sums() {
    return calculateTotal(this.row);
  }

  get isOnDiscount() {
    return isDiscounted(this.row);
  }

  get quantityTooltips() {
    return getTooltips(this.row);
  }

  get hidePrice() {
    return pricesModule.getHidePrice;
  }

  get isSelected() {
    return !this.excludedIds.find((id) => id === this.row.id);
  }

  get currentPriceType() {
    return pricesModule.getCurrentPrice;
  }

  showLoginModal() {
    personalModule.showLoginModal();
  }

  selectedValues({ id, value }: any) {
    this.$emit("select", { id, value });
  }

  rowType() {
    switch (this.row.type) {
      case "product":
        return this.productRow();
      default:
        return this.upperCategory();
    }
  }

  productRow() {
    switch (this.viewType) {
      case "table":
        return this.productRowTable();
      case "file":
        return this.productRowFile();
      default:
        return this.productRowList();
    }
  }

  productRowTable() {
    return this.columns.map((col: Column, ind: number) => (
      <td ind={ind} class={[`data-${col}`]}>
        {this.rowCells(col)}
      </td>
    ));
  }

  renderPriceCell(row: ITableRowExtended, col: Column) {
    // const price = row.promo_price ? row.promo_price : row.price;
    // const classPrice = this.row.promo_price
    //   ? "promo-price"
    //   : this.highlightState(col);

    if (row.price || row.promo_price) {
      return (
        <div class="cell-row nowrap">
          {row.promo_price && !this.hidePrice ? (
            <div>
              <cell-highlighted
                htmlContent={this.highlight(
                  `${row.promo_price?.toLocaleString()} ₽`,
                )}
                class={"promo-price data-price"}
              />
              <span class="divider">/</span>
            </div>
          ) : (
            ""
          )}
          {!this.hidePrice && (
            <cell-highlighted
              htmlContent={this.highlight(`${row.price.toLocaleString()} ₽`)}
              className="data-price"
            />
          )}
        </div>
      );
    } else {
      return this.renderFalsey(col);
    }
  }

  renderCartSKU() {
    if (this.sums.left) {
      return (
        <div class="cell-column">
          <cell-highlighted htmlContent={"Товары по акции"} class="cell-bold" />
          <cell-highlighted
            htmlContent={"Товары без акции"}
            class="cell-bold"
          />
        </div>
      );
    }
  }

  renderCartSum() {
    if (this.sums.left) {
      return (
        <div class="cell-column">
          <cell-highlighted
            class={"data-price"}
            htmlContent={this.highlight(
              `${this.sums.totals[1]?.toLocaleString()} ₽`,
            )}
          />
          <cell-highlighted
            class={"data-price"}
            htmlContent={this.highlight(
              `${this.sums.totals[0]?.toLocaleString()} ₽`,
            )}
          />
        </div>
      );
    }
  }

  renderCartActions() {
    if (this.sums.left) {
      return (
        <div class="cell-column">
          <cell-highlighted
            class={"data-stock"}
            htmlContent={this.highlight(this.row.promo_stock || "") + " ед."}
          />
          <cell-highlighted
            class={"data-stock"}
            htmlContent={this.highlight(this.sums.left || "") + " ед."}
          />
        </div>
      );
    }
  }

  renderFalsey(col: Column) {
    return (
      <div
        class={`tablerow__unautorized cell-data ${this.highlightState(col)}`}
      >
        Под заказ
      </div>
    );
  }

  rowCells(col: Column) {
    const row: ITableRowExtended = this.row;

    switch (col) {
      case CELL_NAME:
        const name = row[col] || "";
        return (
          <cell-name
            id={row.id}
            isOnSale={!!row.onSale}
            isOnDiscount={isDiscounted(row)}
            isTop={!!row.is_top}
            htmlContent={this.highlight(name)}
            class={this.highlightState(CELL_NAME)}
          />
        );
      case CELL_ACTION:
        if (this.isAuthorized) {
          return this.renderAction();
        } else {
          return <cell-action-guest action={this.showLoginModal} />;
        }
      case CELL_MANUFACTURER:
      case CELL_SKU:
        const sku = row[col] || "";
        return (
          <div class="cell-data--column">
            <cell-highlighted
              htmlContent={this.highlight(sku)}
              class={this.highlightState(CELL_SKU)}
            />
            {this.isCart && this.isOnDiscount ? this.renderCartSKU() : null}
          </div>
        );
      case CELL_PRICE:
        if (this.isAuthorized) {
          return this.renderPriceCell(row, col);
        }

        return this.renderFalsey(col);
      case CELL_TOTAL: {
        const sum = this.sums?.totals?.length
          ? this.sums.totals.reduce((a, b) => a + b)
          : "";
        return (
          <div class="cell-column">
            {!this.hidePrice && (
              <cell-highlighted
                htmlContent={`${sum} ₽`}
                class={[this.highlightState(CELL_TOTAL), "data-total"].join(
                  " ",
                )}
              />
            )}
            {this.isCart && this.isOnDiscount && !this.hidePrice
              ? this.renderCartSum()
              : null}
          </div>
        );
      }
      case CELL_STOCK:
        const stock = row.promo_stock
          ? `<span class="table-promo-stock">${row.promo_stock} ед </span> <span class="divider">/</span>${row.stock} ед`
          : `${row.stock} ед`;
        if (this.isAuthorized && row.stock) {
          return (
            <cell-highlighted
              htmlContent={this.highlight(stock)}
              class={this.highlightState(col)}
            />
          );
        }
        return this.renderFalsey(col);
      case CELL_SELECT: {
        return (
          <div class="table-checkbox-wrap">
            <checkbox
              value={this.isSelected}
              returnValue={(value: boolean) =>
                this.selectedValues({ id: this.row.id, value })
              }
              class="table-checkbox"
            />
          </div>
        );
      }
      default:
        return this.renderFalsey(col);
    }
  }

  productRowList() {
    return (
      <table-list
        row={this.row}
        context={this.context}
        quantity={this.quantity}
        isAuthorized={this.isAuthorized}
        isInCart={this.isInCart}
        addToCart={this.cartAddHandler}
        removeFromCart={this.cartRemoveHandler}
        showLoginModal={this.showLoginModal}
        tooltipBreakpoints={this.quantityTooltips}
        isSelected={this.isSelected}
        changeQuantity={this.quantityHandler}
        orderHandler={() => this.$emit("order", this.row, this.quantity)}
        selectedValues={this.selectedValues}
      />
    );
  }

  productRowFile() {
    return <table-file row={this.row} />;
  }

  upperCategory() {
    return this.columns.map((col) => {
      return (
        <upper-cat
          col={col}
          row={this.row}
          htmlContent={
            this.row[col] ? this.highlight(this.row[col]).toString() : ""
          }
        />
      );
    });
  }

  renderAction() {
    const maxCount = this.row.onSale ? this.row.stock : null;
    switch (this.context) {
      case "catalog":
        if (this.isInCart) {
          return (
            <btn
              color="transparent"
              class="button--table"
              border
              onClick={() => this.$router.push("/cart")}
            >
              <div>В корзине!</div>
            </btn>
          );
        } else if (this.row.price) {
          return (
            <div class="cell-column cell-table-actions">
              <cell-action-quantity
                type={this.viewType}
                quantity={this.quantity}
                max={maxCount}
                isOnSale={!!this.row.onSale}
                tooltipBreakpoints={this.quantityTooltips}
                changeHandler={this.quantityHandler}
              >
                <template slot="buttons">
                  {this.row.price ? this.actionButtons() : null}
                </template>
              </cell-action-quantity>
            </div>
          );
        } else {
          return <div></div>;
        }
      default:
        return (
          <div class="cell-column cell-table-actions">
            <cell-action-quantity
              type={this.viewType}
              quantity={this.quantity}
              max={maxCount}
              isOnSale={!!this.row.onSale}
              tooltipBreakpoints={this.quantityTooltips}
              changeHandler={this.quantityHandler}
            >
              <template slot="buttons">
                <buttons-cart action={this.cartRemoveHandler} />
              </template>
            </cell-action-quantity>
            {this.isCart && this.isOnDiscount ? this.renderCartActions() : null}
          </div>
        );
    }
  }

  actionButtons() {
    return [
      <buttons-catalog
        action={this.cartAddHandler}
        icon-title="В корзину"
        icon="cart-add"
      />,
      <span>|</span>,
      <buttons-catalog
        action={() => this.$emit("order", this.row, this.quantity)}
        icon-title="Купить в один клик"
        icon="one-click"
      />,
    ];
  }

  quantityHandler(n: number) {
    this.setQuantity({
      id: this.row.id,
      val: n,
      priceType: this.row.price_type,
    });
  }

  highlight(val: string | number): string {
    const highlight = this.$props.highlightText.toLowerCase();
    const stringVal = val.toString();
    if (highlight === "") {
      return stringVal;
    }
    const from = stringVal.toLowerCase().indexOf(highlight);
    const to = from + highlight.length;

    if (from !== -1) {
      return `${stringVal.substring(0, from)}<a>${stringVal.substring(
        from,
        to,
      )}</a>${stringVal.substring(to)}`;
    }
    return stringVal;
  }

  cartAddHandler() {
    const priceType =
      this.currentPriceType !== "hide" ? this.currentPriceType : 0;
    this.addToCart({
      product_id: this.row.id,
      quantity: this.quantity || 1,
      price_type: priceType,
    });
    this.quantityHandler(1);
  }

  cartRemoveHandler() {
    this.removeFromCart(this.row.id);
  }

  highlightState(key: string) {
    if (!this.highlightColumn) {
      return CELL_DEFAULT;
    }
    if (this.highlightColumn === key) {
      return CELL_ACTIVE;
    }
    return CELL_DIMMED;
  }

  render() {
    return (
      <tr
        class={[`tablerow`, `${this.row.type}-row`, `row-${this.row.index}`]}
        onClick={() => this.$emit("click")}
      >
        {this.rowType()}
      </tr>
    );
  }
}

export default RowPicker;
